/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import {deferMarkForCheck} from '../util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-event-timer',
  template: `
    <span *ngIf="!isRefreshing && countDown >= 1"> Checking {{timerWord}} in {{countDown}} seconds</span>
    <span *ngIf="isRefreshing" class="fa fa-cog fa-spin"></span>
  `
})
export class EventTimerComponent implements OnInit, OnDestroy {
  @Input() timerWord: string;
  @Input() timerStart: string;
  @Output() refreshRequested = new EventEmitter<void>();

  isRefreshing = false;
  countDown = 0;

  private defaultRefreshTime: number;
  private refreshTime: number;
  private newExecutionsTimeout: any;
  private totalTimeTimeout: any;
  private destroyStateListener: () => void;

  constructor(
    private readonly uiRouter: UIRouter,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.defaultRefreshTime = parseInt(this.timerStart || '0', 10) || 1;
    this.timerWord = this.timerWord || 'statuses';

    this.destroyStateListener = this.uiRouter.transitionService.onSuccess({}, () => {
      this.cancelTimers();
      this.destroyStateListener();
    }) as () => void;
  }

  cancelTimers(): void {
    clearTimeout(this.newExecutionsTimeout);
    clearTimeout(this.totalTimeTimeout);
    this.refreshTime = null;
    // Defer countDown clear — sync 1→0 during parent CD NG0100s the *ngIf.
    deferMarkForCheck(this.cdr, () => {
      this.countDown = 0;
    });
  }

  setRefreshing(isRefreshing: boolean): void {
    // Parent report-requester flips this around HTTP; defer past checkNoChanges.
    deferMarkForCheck(this.cdr, () => {
      this.isRefreshing = isRefreshing;
    });
  }

  refreshTimers(suppliedTime?: number): void {
    if (suppliedTime) {
      this.defaultRefreshTime = suppliedTime;
      this.refreshTime = suppliedTime;
    } else if (!this.refreshTime) {
      this.refreshTime = this.defaultRefreshTime;
    } else {
      this.refreshTime = Math.min(Math.ceil(this.refreshTime + (this.refreshTime / 2)), 30);
    }
    clearTimeout(this.totalTimeTimeout);
    this.tick(this.refreshTime);
    clearTimeout(this.newExecutionsTimeout);
    this.newExecutionsTimeout = setTimeout(() => {
      this.refreshRequested.emit();
    }, this.refreshTime * 1000);
  }

  ngOnDestroy(): void {
    clearTimeout(this.newExecutionsTimeout);
    clearTimeout(this.totalTimeTimeout);
    if (this.destroyStateListener) {
      this.destroyStateListener();
    }
  }

  private tick(newCountdown?: number): void {
    const next = Math.max(newCountdown !== undefined ? newCountdown : this.countDown - 1, 1);
    deferMarkForCheck(this.cdr, () => {
      this.countDown = next;
    });
    this.totalTimeTimeout = setTimeout(() => {
      if (this.refreshTime >= this.defaultRefreshTime) {
        this.tick();
      }
    }, 1000);
  }
}
